import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertCircle, Loader2, Save, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '~/lib/api-client';
import { useSession } from '~/lib/auth';
import { buildHead } from '~/seo/site';
import { Toggle } from '~/ui/Toggle';

export const Route = createFileRoute('/settings')({
  head: () =>
    buildHead({
      title: 'Settings',
      description: 'Manage your Prod Battle profile, privacy, and account.',
      path: '/settings',
      noindex: true,
    }),
  component: Settings,
});

type ProfileVisibility = {
  matchHistory: boolean;
  stats: boolean;
  packs: boolean;
  achievements: boolean;
  friends: boolean;
};

type MeResponse = {
  handle: string;
  honor: number;
  profileVisibility: ProfileVisibility;
};

type EmailPrefs = {
  tournament_activity: boolean;
  daily_activity: boolean;
  match_results: boolean;
  honor_alerts: boolean;
  ar_interest: boolean;
  social: boolean;
  account_security: boolean;
  billing: boolean;
};

const EMAIL_PREF_ROWS: Array<{
  key: keyof EmailPrefs;
  label: string;
  description: string;
  locked?: boolean;
}> = [
  {
    key: 'tournament_activity',
    label: 'Tournament reminders',
    description: 'Showcase open, bracket results, and 24h start reminders.',
  },
  {
    key: 'daily_activity',
    label: 'Daily Challenge',
    description: 'Voting-open notification for the Daily Challenge.',
  },
  {
    key: 'match_results',
    label: 'Ranked LP changes',
    description: 'Your LP gain or loss after every ranked match.',
  },
  {
    key: 'honor_alerts',
    label: 'Honor warnings',
    description: 'Alert when your honor drops below 50 and locks ranked features.',
  },
  {
    key: 'ar_interest',
    label: 'A&R interest',
    description: 'When an A&R picks your track, leaves feedback, or reaches out.',
  },
  {
    key: 'social',
    label: 'Friend requests',
    description: 'When someone sends you a friend request or accepts yours.',
  },
  {
    key: 'account_security',
    label: 'Account security',
    description: 'Password resets, email changes, sign-in alerts.',
    locked: true,
  },
  {
    key: 'billing',
    label: 'Billing',
    description: 'Receipts, plan changes, and subscription renewals.',
    locked: true,
  },
];

const VISIBILITY_LABELS: Array<{ key: keyof ProfileVisibility; label: string; help: string }> = [
  {
    key: 'stats',
    label: 'Match stats',
    help: 'Wins, total matches, win rate, streak.',
  },
  {
    key: 'matchHistory',
    label: 'Match history',
    help: 'Recent matches you played, with outcome.',
  },
  {
    key: 'achievements',
    label: 'Achievements',
    help: 'Earned badges. Hide individual ones from your profile detail.',
  },
  {
    key: 'friends',
    label: 'Friends',
    help: 'Show your accepted friends on your public profile.',
  },
  {
    key: 'packs',
    label: 'Pack creator info',
    help: 'Packs you uploaded that made it to the pool.',
  },
];

function Settings() {
  const { data: session, isLoading } = useSession();
  const navigate = useNavigate();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [deleteState, setDeleteState] = useState<'idle' | 'confirming' | 'pending' | 'scheduled'>(
    'idle',
  );
  const [deleteEndsAt, setDeleteEndsAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Email preferences state.
  const [emailPrefs, setEmailPrefs] = useState<EmailPrefs | null>(null);
  const [emailPrefsSaving, setEmailPrefsSaving] = useState(false);
  const [emailPrefsSavedAt, setEmailPrefsSavedAt] = useState<number | null>(null);
  const [emailPrefsError, setEmailPrefsError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    api<MeResponse>('/me')
      .then(setMe)
      .catch((e) => setError((e as Error).message));
  }, [session]);

  useEffect(() => {
    if (!session) return;
    api<EmailPrefs>('/me/email-prefs')
      .then(setEmailPrefs)
      .catch((e) => setEmailPrefsError((e as Error).message));
  }, [session]);

  if (isLoading) return <section className="p-8">Loading...</section>;
  if (!session) {
    return (
      <section className="mx-auto max-w-md px-4 py-16 text-center space-y-3">
        <h2 className="text-2xl font-bold">Sign in to access settings</h2>
        <a href="/auth/sign-in" className="underline">
          Sign in
        </a>
      </section>
    );
  }
  if (!me) {
    return (
      <section className="mx-auto max-w-2xl px-4 py-12 text-muted-foreground">
        {error ? `Couldn't load: ${error}` : 'Loading settings...'}
      </section>
    );
  }

  async function setVisibility(key: keyof ProfileVisibility, value: boolean) {
    if (!me) return;
    const next: ProfileVisibility = { ...me.profileVisibility, [key]: value };
    setMe({ ...me, profileVisibility: next });
    setSaving(true);
    try {
      await api('/me', {
        method: 'PATCH',
        body: JSON.stringify({ profileVisibility: next }),
      });
      setSavedAt(Date.now());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function setEmailPref(key: keyof EmailPrefs, value: boolean) {
    if (!emailPrefs) return;
    // Optimistic update.
    const prev = emailPrefs;
    const next: EmailPrefs = { ...emailPrefs, [key]: value };
    setEmailPrefs(next);
    setEmailPrefsSaving(true);
    try {
      const updated = await api<EmailPrefs>('/me/email-prefs', {
        method: 'PATCH',
        body: JSON.stringify({ [key]: value }),
      });
      setEmailPrefs(updated);
      setEmailPrefsSavedAt(Date.now());
    } catch (e) {
      // Revert on error.
      setEmailPrefs(prev);
      setEmailPrefsError((e as Error).message);
    } finally {
      setEmailPrefsSaving(false);
    }
  }

  async function scheduleDelete() {
    setDeleteState('pending');
    try {
      const r = await api<{ status: string; graceEndsAt: string }>('/me', {
        method: 'DELETE',
      });
      setDeleteEndsAt(r.graceEndsAt);
      setDeleteState('scheduled');
    } catch (e) {
      setError((e as Error).message);
      setDeleteState('idle');
    }
  }

  return (
    <section className="mx-auto max-w-2xl px-4 py-12 space-y-10">
      <header>
        <h2 className="text-3xl font-bold">Settings</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Signed in as <code className="font-mono">@{me.handle}</code>. Honor: {me.honor}/100.
        </p>
      </header>

      {/* ─── Supporter / billing ──────────────────────────────────────── */}
      <BillingSection />

      {/* ─── Privacy ───────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <h3 className="text-xl font-semibold">Profile privacy</h3>
        <p className="text-sm text-muted-foreground">
          Choose which sections appear on your public profile (
          <code className="font-mono">/@{me.handle}</code>).
        </p>
        <div className="space-y-2">
          {VISIBILITY_LABELS.map(({ key, label, help }) => (
            <label
              key={key}
              className="flex items-start gap-3 rounded-md border border-border bg-card px-4 py-3 cursor-pointer hover:border-primary"
            >
              <input
                type="checkbox"
                checked={me.profileVisibility[key]}
                onChange={(e) => setVisibility(key, e.target.checked)}
                className="mt-0.5 size-4 cursor-pointer"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{label}</div>
                <div className="text-xs text-muted-foreground">{help}</div>
              </div>
            </label>
          ))}
        </div>
        {saving && (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Loader2 className="size-3 animate-spin" /> Saving...
          </p>
        )}
        {!saving && savedAt && (
          <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
            <Save className="size-3" /> Saved.
          </p>
        )}
      </section>

      {/* ─── Email notifications ────────────────────────────────────── */}
      <section className="space-y-3">
        <h3 className="text-xl font-semibold">Email notifications</h3>
        <p className="text-sm text-muted-foreground">
          Control which emails Producer Battle sends you. Security and billing emails are always on.
        </p>
        {emailPrefs ? (
          <div className="space-y-3 rounded-md border border-border bg-card px-4 py-4">
            {EMAIL_PREF_ROWS.map(({ key, label, description, locked }) => (
              <Toggle
                key={key}
                id={`email-pref-${key}`}
                checked={emailPrefs[key]}
                onChange={(v) => setEmailPref(key, v)}
                label={label}
                description={description}
                disabled={locked}
              />
            ))}
          </div>
        ) : emailPrefsError ? (
          <p className="text-sm text-destructive">Could not load preferences: {emailPrefsError}</p>
        ) : (
          <p className="text-sm text-muted-foreground">Loading...</p>
        )}
        {emailPrefsSaving && (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Loader2 className="size-3 animate-spin" /> Saving...
          </p>
        )}
        {!emailPrefsSaving && emailPrefsSavedAt && (
          <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
            <Save className="size-3" /> Saved.
          </p>
        )}
      </section>

      {/* ─── Danger zone: delete account ──────────────────────────────── */}
      <section className="space-y-3 rounded-md border border-destructive/30 bg-destructive/5 p-4">
        <h3 className="text-xl font-semibold text-destructive flex items-center gap-2">
          <Trash2 className="size-5" /> Delete account
        </h3>
        <p className="text-sm text-muted-foreground">
          Schedules deletion with a 14-day grace window. Logging in during the grace restores the
          account. After grace, your handle is freed, your submissions are removed, and your match
          history is anonymised.
        </p>
        {deleteState === 'idle' && (
          <button
            type="button"
            onClick={() => setDeleteState('confirming')}
            className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-2 text-sm text-destructive font-medium hover:bg-destructive/20"
          >
            Delete my account
          </button>
        )}
        {deleteState === 'confirming' && (
          <div className="space-y-2">
            <p className="text-sm text-destructive font-medium">
              Are you sure? You'll have 14 days to change your mind by logging back in.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={scheduleDelete}
                className="rounded-md bg-destructive px-4 py-2 text-sm text-destructive-foreground font-medium"
              >
                Yes, schedule deletion
              </button>
              <button
                type="button"
                onClick={() => setDeleteState('idle')}
                className="rounded-md border border-border px-4 py-2 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {deleteState === 'pending' && <p className="text-sm text-muted-foreground">Working...</p>}
        {deleteState === 'scheduled' && (
          <div className="space-y-2 text-sm">
            <p className="text-destructive font-medium">
              Account deletion scheduled. Will be permanent on{' '}
              {deleteEndsAt ? new Date(deleteEndsAt).toLocaleDateString() : 'unknown date'}.
            </p>
            <p className="text-muted-foreground">
              Sign in any time before then to cancel. You'll be signed out automatically when the
              grace ends.
            </p>
            <button
              type="button"
              onClick={() => navigate({ to: '/' })}
              className="rounded-md border border-border px-4 py-2 text-sm"
            >
              Done
            </button>
          </div>
        )}
      </section>

      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm">
          {error}
        </div>
      )}

      <OpenIssues />
    </section>
  );
}

// ─── Supporter / billing ───────────────────────────────────────────────────

type BillingStatus = {
  plan: 'free' | 'paid';
  subscriptionStatus: string | null;
  planExpiresAt: string | null;
};

function BillingSection() {
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Read ?billing=success from the Mollie redirect to show a confirmation.
  const justPaid =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('billing') === 'success';

  function load() {
    api<BillingStatus>('/billing/status')
      .then(setStatus)
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: load once on mount
  useEffect(() => {
    load();
  }, []);

  async function startCheckout(interval: 'monthly' | 'yearly') {
    setError(null);
    setBusy(true);
    try {
      const { checkoutUrl } = await api<{ checkoutUrl: string }>('/billing/checkout', {
        method: 'POST',
        body: JSON.stringify({ interval }),
      });
      window.location.href = checkoutUrl;
    } catch (e) {
      setError((e as Error).message ?? 'Could not start checkout.');
      setBusy(false);
    }
  }

  async function cancel() {
    if (
      !confirm(
        'Cancel your subscription? You keep Supporter access until the period you already paid for ends.',
      )
    )
      return;
    setError(null);
    setBusy(true);
    try {
      await api('/billing/cancel', { method: 'POST' });
      load();
    } catch (e) {
      setError((e as Error).message ?? 'Could not cancel.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return null;

  const isPaid = status?.plan === 'paid';
  const isActive = status?.subscriptionStatus === 'active';
  const isCancelled = isPaid && status?.subscriptionStatus === 'canceled';
  const renews = status?.planExpiresAt ? new Date(status.planExpiresAt).toLocaleDateString() : null;

  return (
    <section className="space-y-3">
      <h3 className="text-xl font-semibold">Supporter</h3>

      {justPaid && (
        <p className="rounded-md border border-green-500/40 bg-green-500/5 px-4 py-3 text-sm text-green-600 dark:text-green-400">
          Welcome to Supporter - your subscription is active. (If your plan below still says Free,
          give it a few seconds and refresh; the payment confirmation arrives via webhook.)
        </p>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}

      {isPaid ? (
        <div className="rounded-md border border-primary/30 bg-primary/5 px-4 py-3 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium">
              {isActive
                ? 'Supporter - active'
                : isCancelled
                  ? 'Supporter - cancelling'
                  : 'Supporter'}
            </span>
            <span className="rounded-full bg-primary/20 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-primary">
              paid
            </span>
          </div>
          {isCancelled && renews && (
            <>
              <p className="text-xs text-muted-foreground">
                Cancelled. Access continues until {renews}, then reverts to Free.
              </p>
              <button
                type="button"
                onClick={() => startCheckout('monthly')}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 font-mono text-xs font-bold uppercase tracking-wider text-primary-foreground disabled:opacity-60"
              >
                {busy && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
                Reactivate - €2.95/mo
              </button>
            </>
          )}
          {isActive && renews && (
            <p className="text-xs text-muted-foreground">Renews on {renews}.</p>
          )}
          {isActive && (
            <button
              type="button"
              onClick={cancel}
              disabled={busy}
              className="rounded-md border border-border px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:border-destructive hover:text-destructive disabled:opacity-50"
            >
              {busy ? 'Working...' : 'Cancel subscription'}
            </button>
          )}
        </div>
      ) : (
        <div className="rounded-md border border-border bg-card px-4 py-3 space-y-3">
          <p className="text-sm text-muted-foreground">
            You're on the Free plan. Supporter unlocks WAV pack downloads, a profile accent color,
            pinned tracks, early genre access, the Supporter badge, and roadmap voting - €2.95/mo.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => startCheckout('monthly')}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 font-mono text-sm font-bold uppercase tracking-wider text-primary-foreground disabled:opacity-60"
            >
              {busy && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
              Become a Supporter - €2.95/mo
            </button>
            <button
              type="button"
              onClick={() => startCheckout('yearly')}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 font-mono text-sm uppercase tracking-wider hover:border-primary disabled:opacity-60"
            >
              Yearly - €28.32 (save 20%)
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// ─── Open Issues / DMCA counter-notice ─────────────────────────────────────

type OpenReport = {
  id: string;
  subjectType: 'submission' | 'profile' | 'pack' | 'genre';
  subjectId: string;
  reason: string;
  notes: string | null;
  createdAt: string;
  subjectLabel: string;
};

function OpenIssues() {
  const [items, setItems] = useState<OpenReport[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api<{ items: OpenReport[] }>('/me/reports')
      .then((r) => setItems(r.items))
      .catch((e) => setErr((e as Error).message));
  }, []);

  if (err || !items) return null;
  if (items.length === 0) return null;

  return (
    <section className="space-y-3 rounded-md border border-yellow-500/40 bg-yellow-500/5 p-4">
      <h3 className="text-xl font-semibold flex items-center gap-2">
        <AlertCircle className="size-5 text-yellow-600 dark:text-yellow-400" />
        Open issues
      </h3>
      <p className="text-sm text-muted-foreground">
        Reports filed against your content. Copyright reports get a counter-notice option below.
      </p>
      <div className="space-y-3">
        {items.map((item) => (
          <OpenIssueRow key={item.id} item={item} />
        ))}
      </div>
    </section>
  );
}

function OpenIssueRow({ item }: { item: OpenReport }) {
  const [counterOpen, setCounterOpen] = useState(false);
  const [statement, setStatement] = useState('');
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function fileCounter() {
    setPending(true);
    setErr(null);
    try {
      await api(`/reports/${encodeURIComponent(item.id)}/counter-notice`, {
        method: 'POST',
        body: JSON.stringify({ statement }),
      });
      setDone(true);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setPending(false);
    }
  }

  const isCopyright = item.reason === 'copyright';

  return (
    <div className="rounded-md border border-border bg-card p-3 space-y-2">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="text-sm">
          <span className="font-medium capitalize">{item.subjectType}</span>
          <span className="ml-2 text-muted-foreground">{item.subjectLabel}</span>
          <span className="ml-3 rounded bg-muted px-2 py-0.5 text-xs font-medium">
            {item.reason}
          </span>
        </div>
        <span className="text-xs text-muted-foreground">
          {new Date(item.createdAt).toLocaleDateString()}
        </span>
      </div>
      {item.notes && (
        <p className="text-xs text-muted-foreground whitespace-pre-wrap">{item.notes}</p>
      )}
      {isCopyright &&
        !done &&
        (!counterOpen ? (
          <button
            type="button"
            onClick={() => setCounterOpen(true)}
            className="text-xs underline text-foreground hover:text-primary"
          >
            File a counter-notice
          </button>
        ) : (
          <div className="space-y-2 pt-1">
            <textarea
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
              rows={4}
              minLength={20}
              maxLength={2000}
              placeholder="Sworn statement: I have a good-faith belief the material was removed in error or misidentified, etc. Add identifying details. (Min 20 chars.)"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            {err && <p className="text-xs text-destructive">{err}</p>}
            <div className="flex gap-2">
              <button
                type="button"
                disabled={pending || statement.length < 20}
                onClick={fileCounter}
                className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
              >
                {pending && <Loader2 className="size-3 animate-spin" />}
                Submit counter-notice
              </button>
              <button
                type="button"
                onClick={() => setCounterOpen(false)}
                className="rounded-md border border-border px-3 py-1.5 text-xs"
              >
                Cancel
              </button>
            </div>
          </div>
        ))}
      {done && (
        <p className="text-xs text-green-600 dark:text-green-400">
          Counter-notice filed. An admin will review both notices and act on either.
        </p>
      )}
    </div>
  );
}
